import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl } from '../config';
import axiosClient from '../lib/axiosClient.ts';
import { addToast, store } from '../store/index.ts';

export type EnquiryFormData = {
  fullname: string;
  phone: string;
  organization: string;
  title: string;
  email: string;
  subject: string;
  otherSubject: string;
  message: string;
};

/** Successful JSON body from POST /api/contact */
export type ContactSubmitApiResponse = {
  success?: boolean;
  notification_email_sent?: boolean;
  mail_failure_hint?: string | null;
  message?: string;
};

type UseEnquirySubmitOptions = {
  onOptimisticSubmit?: (formData: EnquiryFormData) => void;
  onCommitted?: () => void;
  onRollback?: (formData: EnquiryFormData) => void;
  /** API returned 201 but outbound mail did not send. */
  onNotificationFailed?: (data: ContactSubmitApiResponse) => void;
};

type SubmitVariables = {
  formData: EnquiryFormData;
  payload: Record<string, unknown>;
};

type EnquiryToast = {
  type: 'success' | 'error';
  message: string;
  canRetry: boolean;
};

export function useEnquirySubmit(options: UseEnquirySubmitOptions = {}) {
  const queryClient = useQueryClient();
  const lastVariablesRef = useRef<SubmitVariables | null>(null);
  const [toast, setToast] = useState<EnquiryToast | null>(null);

  const mutation = useMutation({
    mutationFn: async ({ payload }: SubmitVariables): Promise<ContactSubmitApiResponse> => {
      const response = await axiosClient.post(apiUrl('/api/contact'), payload);
      return response.data as ContactSubmitApiResponse;
    },
    onMutate: async (variables) => {
      // STEP 1 — snapshot
      const previousData = queryClient.getQueryData(['enquiries']);
      lastVariablesRef.current = variables;

      // STEP 2 — immediate update
      const optimisticEntry = {
        id: `optimistic-${Date.now()}`,
        ...variables.payload,
        optimistic: true,
      };
      queryClient.setQueryData(['enquiries'], (old: unknown) => {
        const current = Array.isArray(old) ? old : [];
        return [optimisticEntry, ...current];
      });

      setToast({
        type: 'success',
        message: "Enquiry received — we'll be in touch.",
        canRetry: false,
      });
      options.onOptimisticSubmit?.(variables.formData);

      return { previousData, formSnapshot: variables.formData };
    },
    onSuccess: (data, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
      if (data && data.notification_email_sent === false) {
        if (context?.previousData !== undefined) {
          queryClient.setQueryData(['enquiries'], context.previousData);
        }
        options.onNotificationFailed?.(data);
        const hint = data.mail_failure_hint;
        const short =
          hint === 'MAIL_NOT_CONFIGURED'
            ? 'Server email is not configured; contact the site administrator.'
            : hint === 'GRAPH_AUTH_FAILED' ||
                hint === 'GRAPH_FORBIDDEN' ||
                hint === 'GRAPH_INVALID_SENDER'
              ? 'Email could not be sent (Microsoft Graph). Please call us.'
              : 'Your enquiry was received, but the notification email could not be sent.';
        store.dispatch(
          addToast({
            type: 'error',
            message: short,
            autoDismiss: false,
          }),
        );
        setToast({
          type: 'error',
          message: short,
          canRetry: true,
        });
        return;
      }
      options.onCommitted?.();
    },
    onError: (_error, _variables, context) => {
      // STEP 5 — rollback
      queryClient.setQueryData(['enquiries'], context?.previousData);
      options.onRollback?.(context?.formSnapshot as EnquiryFormData);
      store.dispatch(
        addToast({
          type: 'error',
          message: 'Could not submit enquiry. Please try again.',
          autoDismiss: false,
        })
      );
      setToast({
        type: 'error',
        message: 'Could not submit enquiry. Please try again.',
        canRetry: true,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
    },
  });

  const retry = () => {
    if (lastVariablesRef.current) {
      mutation.mutate(lastVariablesRef.current);
    }
  };

  return {
    submitEnquiry: mutation.mutate,
    isSubmitting: mutation.isPending,
    toast,
    clearToast: () => setToast(null),
    retry,
  };
}
